import { FC } from "react";
import { Field, FieldArray, useFormikContext } from "formik";
import { IconButton, MenuItem } from "@mui/material";
import DeleteIcon from '@mui/icons-material/Delete';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import Grid from '@mui/material/Grid2';
import { Input } from '@components/input';
import { CreateProjectForm } from '@pages/create-project/create-project';
import { useTranslation } from 'react-i18next';
import { SectionWrapper } from "@components/ui-kit/SectionWrapper";

export const BuildingStep: FC = () => {
  const {t} = useTranslation();
  const {values, errors, touched} = useFormikContext<CreateProjectForm>();

  const calculateRoofAreaSum = () => {
    return values.pv_basic_power_and_module_determination.reduce((sum, item) => {
      const roofArea = parseFloat(item.roof_area as string) || 0;
      return sum + roofArea;
    }, 0);
  };

  const calculateUseableRoofAreaSum = () => {
    return values.pv_basic_power_and_module_determination.reduce((sum, item) => {
      const roofArea = parseFloat(item.roof_area as string) || 0;
      const useablePercentage = parseFloat(item.useable_roof_area as string) || 0;

      // Calculate actual useable area by applying the percentage to the roof area
      const useableArea = roofArea * (useablePercentage / 100);
      return sum + useableArea;
    }, 0);
  };

  const buildingStatuses = [
    {value: 'unsaniert', label: 'unsaniert'},
    {value: 'nur Fenstertausch/ nur Fassade', label: 'nur Fenstertausch/ nur Fassade'},
    {value: 'komplett saniert (F+F)', label: 'komplett saniert (F+F)'},
    {value: 'Neubau', label: 'Neubau'},
  ];

  const infrastructureTypes = [
    {value: 'Heizkörper alt', label: 'Heizkörper alt'},
    {value: 'Heizkörper neu', label: 'Heizkörper neu'},
    {value: 'Fußbodenheizung', label: 'Fußbodenheizung'},
  ];

  const orientations = [
    {value: 'S', label: 'S'},
    {value: 'SO/SW', label: 'SO/SW'},
    {value: 'O/W', label: 'O/W'},
  ];

  const inclinations = [
    {value: '0 Grad', label: '0 Grad'},
    {value: '45 Grad', label: '45 Grad'},
    {value: '90 Grad', label: '90 Grad'},
  ];

  return (
    <div className="flex flex-col gap-4">
      {/* Surfaces block */}
      <SectionWrapper title={t("project.surfacesBlockTitle")} className="gap-2">
        <Grid container spacing={4}>
          <Grid size={4}>
            <Field
              as={Input}
              label={t("project.projectName")}
              name="project_name"
              errors={errors}
              touched={touched}
              fullWidth
              isRequired
            />
          </Grid>

          <Grid size={4}>
            <Field
              as={Input}
              label={t("project.projectDescription")}
              name="project_description"
              fullWidth
              errors={errors}
              touched={touched}
            />
          </Grid>

          <Grid size={4}>
            <Field
              as={Input}
              label={t("project.customerName")}
              name="customer_name"
              fullWidth
              errors={errors}
              touched={touched}
            />
          </Grid>
        </Grid>

        <Grid container spacing={4}>
          <Grid size={3}>
            <Field
              as={Input}
              label={t("project.postalCode")}
              name="postal_code"
              fullWidth
              errors={errors}
              touched={touched}
            />
          </Grid>

          <Grid size={3}>
            <Field
              as={Input}
              label={t("project.heatedArea")}
              name="heated_area"
              fullWidth
              type="number"
              adornment="M^2"
              errors={errors}
              touched={touched}
              isRequired
            />
          </Grid>

          <Grid size={3}>
            <Field
              as={Input}
              label={t("project.numberOfUnits")}
              name="number_of_units"
              fullWidth
              type="number"
              adornment="M^2"
              errors={errors}
              touched={touched}
              isRequired
            />
          </Grid>

          <Grid size={3}>
            <Field
              as={Input}
              label={t("project.exteriorArea")}
              name="exterior_area"
              fullWidth
              type="number"
              adornment="M^2"
              errors={errors}
              touched={touched}
              isRequired
            />
          </Grid>
        </Grid>
      </SectionWrapper>

      {/* Building block */}
      <SectionWrapper title={t("project.buildingBlockTitle")}>
        <Grid container spacing={4}>
          <Grid size={3}>
            <Field
              as={Input}
              label={t("project.buildingYear")}
              name="building_year"
              type="number"
              fullWidth
              errors={errors}
              touched={touched}
              isRequired
            />
          </Grid>
          <Grid size={3}>
            <Field
              as={Input}
              select
              label={t("project.buildingCurrentStatus")}
              name="building_current_status"
              errors={errors}
              touched={touched}
              fullWidth
            >
              {buildingStatuses.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {t(option.label)}
                </MenuItem>
              ))}
            </Field>
          </Grid>
          <Grid size={3}>
            <Field
              as={Input}
              label={t("project.hwbFromEa")}
              name="hwb_from_ea_as_planned"
              type="number"
              adornment="kWh/m2*a"
              fullWidth
              errors={errors}
              touched={touched}
            />
          </Grid>
          <Grid size={3}>
            <Field
              as={Input}
              select
              label={t("project.typeOfInfrastructure")}
              name="type_of_infrastructure"
              errors={errors}
              touched={touched}
              fullWidth
              isRequired
            >
              {infrastructureTypes.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {t(option.label)}
                </MenuItem>
              ))}
            </Field>
          </Grid>
        </Grid>
      </SectionWrapper>

      {/* Roof block */}
      <SectionWrapper title={t("project.roofAreasBlockTitle")}>
        <FieldArray
          name="pv_basic_power_and_module_determination"
          render={arrayHelpers => (
            <div>
              <div className="flex flex-col gap-4">
                {values.pv_basic_power_and_module_determination.map((_, index) => (
                  <div key={index}>
                    <div className="flex items-center mb-2">
                      <h4 className="font-medium">{t("project.roofArea")} #{index + 1}</h4>
                      {values.pv_basic_power_and_module_determination.length > 1 && (
                        <IconButton
                          onClick={() => arrayHelpers.remove(index)}
                          size="small"
                          color="error"
                        >
                          <DeleteIcon/>
                        </IconButton>
                      )}
                      {index === values.pv_basic_power_and_module_determination.length - 1 ? (
                          <IconButton
                            onClick={() => arrayHelpers.push({
                              roof_area: "",
                              useable_roof_area: "",
                              orientation: "",
                              inclination_of_roof: ""
                            })}
                            color="primary"
                            size="small"
                          >
                            <AddCircleOutlineIcon/>
                          </IconButton>)
                        : null}
                    </div>
                    <Grid container spacing={3}>
                      <Grid size={3}>
                        <Field
                          as={Input}
                          label={t("project.roofArea")}
                          name={`pv_basic_power_and_module_determination.${index}.roof_area`}
                          fullWidth
                          type="number"
                          adornment="M^2"
                          errors={errors}
                          touched={touched}
                        />
                      </Grid>

                      <Grid size={3}>
                        <Field
                          as={Input}
                          label={t("project.usableRoofArea")}
                          name={`pv_basic_power_and_module_determination.${index}.useable_roof_area`}
                          fullWidth
                          value={values.pv_basic_power_and_module_determination[index].useable_roof_area}
                          type="number"
                          adornment="%"
                          errors={errors}
                          touched={touched}
                        />
                      </Grid>

                      <Grid size={3}>
                        <Field
                          as={Input}
                          select
                          label={t("project.orientation")}
                          name={`pv_basic_power_and_module_determination.${index}.orientation`}
                          value={values.pv_basic_power_and_module_determination[index].orientation}
                          errors={errors}
                          touched={touched}
                          fullWidth
                          isRequired={!!values.pv_basic_power_and_module_determination[index].roof_area}
                        >
                          {orientations.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {t(option.label)}
                            </MenuItem>
                          ))}
                        </Field>
                      </Grid>

                      <Grid size={3}>
                        <Field
                          as={Input}
                          select
                          label={t("project.inclinationOfRoof")}
                          name={`pv_basic_power_and_module_determination.${index}.inclination_of_roof`}
                          value={values.pv_basic_power_and_module_determination[index].inclination_of_roof}
                          fullWidth
                          errors={errors}
                          touched={touched}
                          isRequired={!!values.pv_basic_power_and_module_determination[index].roof_area}
                        >
                          {inclinations.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {t(option.label)}
                            </MenuItem>
                          ))}
                        </Field>
                      </Grid>
                    </Grid>
                  </div>
                ))}
              </div>

              {/* Sum fields - only visible when there are 2 or more rows */}
              {values.pv_basic_power_and_module_determination.length >= 2 && (
                <div className="mt-6 pt-4 border-t border-gray-200">
                  <Grid container spacing={3}>
                    <Grid size={3}>
                      <Input
                        label={t("project.totalRoofArea")}
                        name={t("project.totalRoofArea")}
                        value={calculateRoofAreaSum()}
                        fullWidth
                        adornment="M^2"
                        disabled
                      />
                    </Grid>
                    <Grid size={3}>
                      <Input
                        label={t("project.totalUsableRoofArea")}
                        name={t("project.totalUsableRoofArea")}
                        value={calculateUseableRoofAreaSum()}
                        fullWidth
                        adornment="M^2"
                        disabled
                      />
                    </Grid>
                  </Grid>
                </div>
              )}
            </div>
          )}
        />
      </SectionWrapper>

      {/* Comment block */}
      <SectionWrapper>
        <Grid size={12}>
          <div className="flex justify-center w-full">
            <Field
              className="w-1/2"
              as={Input}
              multiline
              rows={6}
              label={t("project.generalComment")}
              name="general_comment"
              value={values.general_comment}
              errors={errors}
              touched={touched}
            />
          </div>
        </Grid>
      </SectionWrapper>
    </div>
  )
};
